/**
 * O aviso da ficha sem CNPJ que recebeu orçamento (13.5, FR-27 emendado, PN5; textos da 13.11c).
 *
 * Antes dele, a Empresa sem documento que recebia Orçamento ficava fora do Carga Máquina **em
 * silêncio**: o Carga Máquina casa por CNPJ, e a ida só deixava uma linha de log. Agora o `cadastro`
 * guarda a marca, a ficha diz o que fazer, e o Responsável ou a gestão que salvar a ficha com o CNPJ
 * a manda ao Carga Máquina na mesma gravação.
 *
 * - **Sem documento:** "Esta empresa recebeu orçamento. Preencha o CNPJ para cadastrar no Carga
 *   Máquina." — o texto decidido pelo Gustavo em 22/10/2026, literal.
 * - **Com documento** (outra pessoa o preencheu pela PA-1 e a ida não aconteceu — PN5, D-18 da
 *   13.11b —, ou o CNPJ é de outra ficha): diz quem a cadastra, e aponta a troca da Empresa do
 *   Negócio para a ficha certa (D-9 da 13.5).
 *
 * - **Para quem não é o Responsável nem a gestão** (edita pela PA-1 ou pelo Grupo, 13.14a, DW-509):
 *   o CNPJ não é dele — o aviso diz quem o preenche, sem pedir o que a tela não deixa fazer.
 *
 * Ele lê a **mesma** chave da ficha (`useEmpresa`): a gravação que manda a Empresa ao Carga Máquina
 * atualiza as duas, e o aviso some sem consulta nova. Não aparece com a marca ausente, nem enquanto a
 * ficha carrega, nem quando ela não chega. Quem é "de outro" diz a resposta (`quem_le_e_o_dono`,
 * 13.14b), e não a sessão.
 *
 * **Qual Negócio trouxe o orçamento** (13.14b, DW-409): a marca do `cadastro` é um instante e não
 * guarda o Negócio (D-6 da 13.5); o aviso compõe pelo SPA (AD-25) com os Negócios da Empresa que
 * quem lê enxerga (`GET /negocios/das-empresas`), os de `com_orcamento`, de qualquer status, por
 * código — até cinco, e "e mais N". Cada código abre o modal **sobre a ficha** (`LinkDoNegocio`).
 * Sem nenhum visível (ou com a leitura vazia, em voo ou falha), o texto de sempre, sem a frase.
 * Excluir o Negócio pelo modal tira a marca quando o `cadastro` consome o `negocio_excluido` (D-4).
 */

import { useNegociosDasEmpresas } from '@/modulos/negocios/api'
import { LinkDoNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { useEmpresa } from './api'
import './aviso-do-orcamento-sem-cnpj.css'

export const AVISO_SEM_DOCUMENTO =
  'Esta empresa recebeu orçamento. Preencha o CNPJ para cadastrar no Carga Máquina.'

export const AVISO_COM_DOCUMENTO =
  'Esta empresa recebeu orçamento e ainda não está no Carga Máquina. Ela é cadastrada lá quando ' +
  'o Responsável ou a gestão salvar a ficha com o CNPJ; se o CNPJ é de outra ficha, a gestão troca ' +
  'a Empresa do Negócio para a ficha certa.'

export const AVISO_PARA_QUEM_NAO_E_O_RESPONSAVEL =
  'Esta empresa recebeu orçamento. O Responsável por ela ou a gestão preenchem o CNPJ para ' +
  'cadastrá-la no Carga Máquina.'

/** Quantos códigos de Negócio a frase mostra; o resto vira "e mais N". */
export const CODIGOS_NA_FRASE = 5

/** O começo e o fim da frase do Negócio — os códigos (links) vão no meio. */
export function fraseDosNegocios(total: number): [string, string] {
  const comeco =
    total === 1 ? 'O orçamento veio do Negócio ' : 'Os orçamentos vieram dos Negócios '
  const resto = total - CODIGOS_NA_FRASE
  return [comeco, resto > 0 ? ` e mais ${String(resto)}.` : '.']
}

/** O separador antes do código de posição `indice` entre `mostrados` (com ou sem "e mais"). */
function separador(indice: number, mostrados: number, comResto: boolean): string {
  if (indice === 0) return ''
  return indice === mostrados - 1 && !comResto ? ' e ' : ', '
}

export interface PropriedadesDoAviso {
  empresaId: string
}

export function AvisoDoOrcamentoSemCnpj({ empresaId }: PropriedadesDoAviso) {
  const ficha = useEmpresa(empresaId).data
  const marcada = ficha?.recebeu_orcamento_sem_documento === true
  const negocios = useNegociosDasEmpresas(marcada ? [empresaId] : [])
  if (!marcada) {
    return null
  }
  const deOutro = !ficha.quem_le_e_o_dono
  const comOrcamento = (Array.isArray(negocios.data) ? negocios.data : [])
    .filter((negocio) => negocio.com_orcamento && negocio.empresa_id === empresaId)
    .sort((a, b) => a.codigo - b.codigo)
  const mostrados = comOrcamento.slice(0, CODIGOS_NA_FRASE)
  const [comeco, fim] = fraseDosNegocios(comOrcamento.length)
  const comResto = comOrcamento.length > CODIGOS_NA_FRASE
  return (
    <p className="ficha-da-empresa__aviso" role="status">
      {ficha.documento === null
        ? deOutro
          ? AVISO_PARA_QUEM_NAO_E_O_RESPONSAVEL
          : AVISO_SEM_DOCUMENTO
        : AVISO_COM_DOCUMENTO}
      {mostrados.length === 0 ? null : (
        <>
          {` ${comeco}`}
          {mostrados.map((negocio, indice) => (
            <span key={negocio.id}>
              {separador(indice, mostrados.length, comResto)}
              <LinkDoNegocio negocioId={negocio.id}>{`#${String(negocio.codigo)}`}</LinkDoNegocio>
            </span>
          ))}
          {fim}
        </>
      )}
    </p>
  )
}
